import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';

import { useGetDepartmentsQuery } from '@/API/departments.api';
import { FEATURE_FLAGS_ENUM } from '@/constants/config';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { initializeDepartmentsAndTemplates } from '@/store/slices/departmentsAndTemplates.slice';
import { DepartmentSlim } from '@/types/department.types';
import { TemplateSlim } from '@/types/template.types';

const useDepartments = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const flags = useFlags();
  const isNoDepartmentMRCEnabled = flags[FEATURE_FLAGS_ENUM.LV15455NVMNoDepartmentMRC];

  const { data } = useGetDepartmentsQuery(undefined, {
    skip: !isAuthenticated,
  });

  /**
   * Subscribing here (rather than only in the access drawers that read it) keeps this cache entry warm for
   * the whole session the same way the no-arg entry above is. Both of its actual consumers - ViewAccessControls
   * and PersonnelMembershipDrawer - only mount while their drawer is open, so without a permanent subscriber
   * RTK Query would evict it (keepUnusedDataFor) between opens and refetch the full tree on every cold open
   * instead of reading from cache like every other departments dropdown does.
   */
  useGetDepartmentsQuery({ excludeMRC: true }, {
    skip: !isAuthenticated || !isNoDepartmentMRCEnabled,
  });

  const dispatch = useAppDispatch();

  return React.useCallback(() => {
    if (!isAuthenticated) return;

    const departmentIdTemplateMap: { [key: number]: number[] } = {};

    const departments: DepartmentSlim[] = [];
    const templates: Map<number, TemplateSlim> = new Map();

    data?.forEach((department) => {
      departments.push({ departmentId: department.department_id, departmentName: department.name });
      department.templates.forEach((template) => {
        if (!departmentIdTemplateMap?.[department.department_id]) {
          departmentIdTemplateMap[department.department_id] = [template.template_id];
        } else {
          const currentDepartmentTemplates = departmentIdTemplateMap?.[department.department_id] ?? [];

          departmentIdTemplateMap[department.department_id] = [
            ...currentDepartmentTemplates,
            template.template_id,
          ];
        }

        if (!templates.has(template.template_id)) {
          templates.set(template.template_id, {
            templateId: template.template_id,
            templateName: template.name,
          });
        }
      });
    });

    dispatch(initializeDepartmentsAndTemplates({
      departmentIdTemplateMap,
      departments,
      templates: Array.from(templates.values()),
    }));
  }, [data, dispatch, isAuthenticated]);
};

export default useDepartments;
